import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Bug, Home, RefreshCw, Bell, CheckCircle2, AlertTriangle } from 'lucide-react';
import { enableWebPushAndTest, getWebPushDebugSnapshot, regenerateWebPushToken } from '../../utils/pushNotifications';

function Row({ label, value, ok }) {
  const color = ok === true ? '#6ee7b7' : ok === false ? '#fca5a5' : 'rgba(255,255,255,0.75)';
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
      <div style={{ fontWeight: 700, color: 'rgba(255,255,255,0.8)' }}>{label}</div>
      <div style={{ color, textAlign: 'right', wordBreak: 'break-word', maxWidth: 520 }}>{value}</div>
    </div>
  );
}

export default function PushDebugPage() {
  const navigate = useNavigate();
  const [busy, setBusy] = React.useState(false);
  const [snapshot, setSnapshot] = React.useState(null);
  const [msg, setMsg] = React.useState('');

  const refresh = React.useCallback(async () => {
    const token = localStorage.getItem('token');
    const s = await getWebPushDebugSnapshot(token);
    setSnapshot(s);
  }, []);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const enableAndTest = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setMsg('Not logged in.');
      return;
    }
    setBusy(true);
    setMsg('');
    try {
      const res = await enableWebPushAndTest(token);
      if (res.ok) setMsg('Enabled. Test push sent.');
      else setMsg(res.message || res.reason || 'Enable failed.');
    } finally {
      setBusy(false);
      await refresh();
    }
  };

  const regenerateToken = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setMsg('Not logged in.');
      return;
    }
    setBusy(true);
    setMsg('');
    try {
      const res = await regenerateWebPushToken(token);
      if (res.ok) setMsg('FCM token regenerated successfully.');
      else setMsg(res.message || res.reason || 'Regeneration failed.');
    } finally {
      setBusy(false);
      await refresh();
    }
  };

  const permission = snapshot?.permission || 'unknown';
  const swActive = !!snapshot?.serviceWorker?.active;
  const tokenPresent = !!snapshot?.storedTokenPresent;
  const backendConfigured = !!snapshot?.backend?.pushConfigured;
  const tokenSaved = !!snapshot?.backend?.tokenSaved;

  const overallOk = permission === 'granted' && swActive && tokenPresent && tokenSaved && backendConfigured;

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)', color: '#fff', padding: '24px 16px' }}>
      <div style={{ maxWidth: 980, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 44, height: 44, borderRadius: 14, background: 'rgba(148,163,184,0.14)', border: '1px solid rgba(148,163,184,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Bug size={20} color="#cbd5e1" />
            </div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 900, letterSpacing: '-0.2px' }}>Push Debug</div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>Verify web push end-to-end (permission → SW → FCM token → backend → Firebase).</div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => navigate('/home')}
              style={{ width: 44, height: 44, borderRadius: 12, border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.08)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              title="Home"
            >
              <Home size={20} />
            </button>
            <button
              type="button"
              onClick={refresh}
              disabled={busy}
              style={{ padding: '10px 14px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.08)', color: '#fff', cursor: busy ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 800 }}
            >
              <RefreshCw size={16} style={busy ? { animation: 'spin 1s linear infinite' } : {}} />
              Refresh
            </button>
            <button
              type="button"
              onClick={enableAndTest}
              disabled={busy}
              style={{ padding: '10px 14px', borderRadius: 12, border: 'none', background: busy ? 'rgba(59,130,246,0.25)' : 'linear-gradient(135deg,#3b82f6,#2563eb)', color: '#fff', cursor: busy ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 900 }}
            >
              <Bell size={16} />
              Enable + Send Test
            </button>
            <button
              type="button"
              onClick={regenerateToken}
              disabled={busy}
              style={{ padding: '10px 14px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.08)', color: '#fff', cursor: busy ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 800 }}
            >
              <RefreshCw size={16} style={busy ? { animation: 'spin 1s linear infinite' } : {}} />
              Regenerate FCM Token
            </button>
          </div>
        </div>

        {msg && (
          <div style={{ marginTop: 14, padding: '10px 12px', borderRadius: 12, background: msg.includes('Enabled') ? 'rgba(16,185,129,0.14)' : 'rgba(239,68,68,0.14)', border: `1px solid ${msg.includes('Enabled') ? 'rgba(16,185,129,0.35)' : 'rgba(239,68,68,0.35)'}` }}>
            {msg}
          </div>
        )}

        <div style={{ marginTop: 18, borderRadius: 18, border: '1px solid rgba(255,255,255,0.12)', overflow: 'hidden', background: 'rgba(255,255,255,0.04)' }}>
          <div style={{ padding: '12px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ fontWeight: 900 }}>Overall status</div>
            {overallOk ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 900, background: 'rgba(16,185,129,0.18)', border: '1px solid rgba(16,185,129,0.35)', color: '#6ee7b7' }}>
                <CheckCircle2 size={14} /> OK
              </span>
            ) : (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 900, background: 'rgba(251,191,36,0.16)', border: '1px solid rgba(251,191,36,0.35)', color: '#fde68a' }}>
                <AlertTriangle size={14} /> Needs attention
              </span>
            )}
          </div>

          <Row label="Notification permission" value={permission} ok={permission === 'granted' ? true : permission === 'denied' ? false : null} />
          <Row label="Service worker supported" value={snapshot?.serviceWorker?.supported ? 'yes' : 'no'} ok={!!snapshot?.serviceWorker?.supported} />
          <Row label="Service worker registered" value={snapshot?.serviceWorker?.registered ? 'yes' : 'no'} ok={!!snapshot?.serviceWorker?.registered} />
          <Row label="Service worker active" value={swActive ? 'yes' : 'no'} ok={swActive} />
          <Row label="Service worker scope" value={snapshot?.serviceWorker?.scope || '—'} />
          <Row label="Service worker script" value={snapshot?.serviceWorker?.scriptURL || '—'} />
          <Row label="Production VAPID key exists" value={snapshot?.vapidKeyExists ? 'yes' : 'no'} ok={snapshot?.vapidKeyExists === true} />
          <Row label="FCM token generated (stored)" value={tokenPresent ? `yes (${snapshot?.storedTokenPreview || 'token'})` : 'no'} ok={tokenPresent} />
          <Row label="Backend tokenSaved" value={tokenSaved ? 'yes' : 'no'} ok={tokenSaved} />
          <Row label="Backend activeTokens" value={snapshot?.backend?.activeTokens != null ? String(snapshot.backend.activeTokens) : '—'} />
          <Row label="Backend Firebase pushConfigured" value={backendConfigured ? 'yes' : 'no'} ok={backendConfigured} />
          <Row label="Backend Firebase status" value={snapshot?.backend?.firebase?.status || '—'} ok={snapshot?.backend?.firebase?.status === 'ready' ? true : snapshot?.backend?.firebase?.status ? false : null} />
          <Row label="Backend Firebase reason" value={snapshot?.backend?.firebase?.reason || '—'} />
          <Row label="Last push-test result" value={snapshot?.lastTest ? JSON.stringify(snapshot.lastTest).slice(0, 220) : '—'} />
          <Row label="Last local push error reason" value={snapshot?.lastError?.reason || '—'} ok={snapshot?.lastError ? false : null} />
          <Row label="Last local push error code" value={snapshot?.lastError?.code || '—'} ok={snapshot?.lastError?.code ? false : null} />
          <Row label="Last local push error message" value={snapshot?.lastError?.message || '—'} ok={snapshot?.lastError ? false : null} />
          <Row label="iOS device" value={snapshot?.ios ? 'yes' : 'no'} />
          <Row label="PWA standalone" value={snapshot?.standalone ? 'yes' : 'no'} ok={snapshot?.ios ? snapshot?.standalone : null} />
        </div>

        <style>{`
          @keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
        `}</style>
      </div>
    </div>
  );
}

